import { Share2, Twitter, Facebook, Linkedin, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareButtons — one-click share to X, Facebook, WhatsApp, LinkedIn, Reddit,
 * plus Copy Link. Now with **rich pre-fills**: pass `kind` + a `subject` and
 * the share copy sings instead of dumping a bare URL.
 *
 *   kind="artist"  subject={ name }
 *   kind="track"   subject={ title, artist_name }
 *   kind="album"   subject={ title, artist_name }
 *   kind=undefined → generic MeloCraft copy
 *
 * Every share still shows the same URL/title props for callers that want
 * full control (legacy usage stays compatible).
 */
export default function ShareButtons({ url, title, kind, subject, hashtags = "MeloCraft,IndieMusic", preview }) {
  // If the caller passes `preview={{kind:'album', id:'xyz'}}` we share the
  // backend OG URL instead of the SPA route — this lets Facebook/Twitter/etc
  // scrape per-entity Open Graph tags (title, cover, artist) since crawlers
  // don't run JS. Humans clicking the link get redirected to the real page.
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const previewUrl = preview && preview.kind && preview.id
    ? `${API}/api/og/${preview.kind}/${preview.id}`
    : null;
  const shareUrl = previewUrl || url || (typeof window !== "undefined" ? window.location.href : "");
  const rich = buildShareCopy({ kind, subject, fallback: title });
  const t = encodeURIComponent(rich.text);
  const u = encodeURIComponent(shareUrl);
  const links = [
    { key: "x", label: "X", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${t}&url=${u}&hashtags=${hashtags}` },
    { key: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}` },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${t}%20${u}` },
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { key: "reddit", label: "Reddit", icon: Share2, href: `https://www.reddit.com/submit?url=${u}&title=${t}` },
  ];

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(`${rich.text}\n${shareUrl}`); toast.success("Caption + link copied — paste anywhere"); }
    catch { toast.error("Couldn't copy"); }
  };

  return (
    <div data-testid="share-buttons" className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mr-1">Share:</span>
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <a key={l.key} data-testid={`share-${l.key}`} href={l.href}
             target="_blank" rel="noopener noreferrer" title={`Share on ${l.label}`}
             className="h-9 w-9 grid place-items-center border border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors">
            <Icon className="h-4 w-4" />
          </a>
        );
      })}
      <button data-testid="share-copy" onClick={copyLink} title="Copy link only"
              className="h-9 px-3 border border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] font-mono text-[10px] uppercase tracking-widest transition-colors">
        Copy link
      </button>
      {/* Instagram doesn't support pre-fill via URL — best UX is copy caption + link
         to paste into a Story or Post. Also handy for TikTok / anywhere else. */}
      <button data-testid="share-copy-caption" onClick={copyCaption} title="Copy caption + link (for Instagram, TikTok, etc.)"
              className="h-9 px-3 border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] font-mono text-[10px] uppercase tracking-widest transition-colors">
        Copy caption
      </button>
    </div>
  );
}

/** Build a punchy share caption that reads like a fan post, not a bot dump.
 *  Rotates between two variants per kind so a page shared twice doesn't
 *  yield identical copy (small deduping trick to avoid Facebook flagging). */
function buildShareCopy({ kind, subject, fallback }) {
  const s = subject || {};
  const roll = Math.floor(Math.random() * 2);
  if (kind === "artist" && s.name) {
    const opts = [
      `🎧 Just found ${s.name} on MeloCraft — every dollar goes straight to the artist. Give them a spin.`,
      `Real indie music, no middleman. Listen to ${s.name} on MeloCraft — songs $0.99, whole records $15.`,
    ];
    return { text: opts[roll] };
  }
  if (kind === "track" && s.title) {
    const opts = [
      `🔥 "${s.title}"${s.artist_name ? ` by ${s.artist_name}` : ""} — hear it on MeloCraft. Artist keeps 100%.`,
      `New drop: "${s.title}"${s.artist_name ? ` — ${s.artist_name}` : ""} on MeloCraft. $0.99 per song, direct to the artist.`,
    ];
    return { text: opts[roll] };
  }
  if (kind === "album" && s.title) {
    const opts = [
      `📀 Full record — "${s.title}"${s.artist_name ? ` by ${s.artist_name}` : ""} — $15 on MeloCraft. Every dollar to the artist.`,
      `Press play on "${s.title}"${s.artist_name ? ` — ${s.artist_name}` : ""}. Independent, direct, and $15 for the whole album on MeloCraft.`,
    ];
    return { text: opts[roll] };
  }
  return { text: fallback || "Check this out on MeloCraft — the artist-first music market." };
}
